/**
 * Bases regionais curadas (dados, não regras).
 *
 * Usadas pelo planeador hierárquico para propor 2–3 bases realistas quando o
 * pedido é de âmbito nacional/regional. Se um país não tiver entradas, o
 * planeador pergunta em vez de inventar.
 */

export type RegionalBase = {
  id: string;
  countryCode: string;
  /** Cidade/vila onde o viajante fica alojado. */
  city: string;
  /** Região turística associada. */
  region: string;
  lat: number;
  lng: number;
  /** Temas cobertos a partir desta base. */
  themes: Array<"nature" | "lakes" | "mountains" | "cultural" | "food" | "coast" | "city" | "scenic_rail">;
  /** Nível de preço relativo de alojamento (1 = mais barato). */
  affordability: 1 | 2 | 3;
  /** Boa ligação ferroviária/transportes públicos. */
  transitFriendly: boolean;
  note?: string;
};

export const REGIONAL_BASES: RegionalBase[] = [
  // Suíça
  { id: "ch-interlaken", countryCode: "CH", city: "Interlaken", region: "Oberland Bernês", lat: 46.6863, lng: 7.8632, themes: ["lakes", "mountains", "nature", "scenic_rail"], affordability: 2, transitFriendly: true, note: "Entre os lagos Thun e Brienz; base natural para lagos e montanha." },
  { id: "ch-lucerne", countryCode: "CH", city: "Lucerna", region: "Suíça Central", lat: 47.0502, lng: 8.3093, themes: ["lakes", "city", "cultural", "scenic_rail"], affordability: 2, transitFriendly: true },
  { id: "ch-montreux", countryCode: "CH", city: "Montreux", region: "Lago Léman", lat: 46.4312, lng: 6.9107, themes: ["lakes", "scenic_rail", "food"], affordability: 2, transitFriendly: true },
  { id: "ch-lugano", countryCode: "CH", city: "Lugano", region: "Ticino", lat: 46.0037, lng: 8.9511, themes: ["lakes", "food", "city"], affordability: 2, transitFriendly: true },
  { id: "ch-chur", countryCode: "CH", city: "Chur", region: "Graubünden", lat: 46.8508, lng: 9.5320, themes: ["mountains", "scenic_rail", "nature"], affordability: 1, transitFriendly: true, note: "Base económica e ponto de partida do Bernina/Glacier Express." },
  { id: "ch-brig", countryCode: "CH", city: "Brig", region: "Valais", lat: 46.3167, lng: 7.9880, themes: ["mountains", "scenic_rail"], affordability: 1, transitFriendly: true },
  { id: "ch-zermatt", countryCode: "CH", city: "Zermatt", region: "Valais", lat: 46.0207, lng: 7.7491, themes: ["mountains", "nature", "scenic_rail"], affordability: 3, transitFriendly: true, note: "Terminal ocidental do Glacier Express." },
  { id: "ch-stmoritz", countryCode: "CH", city: "St. Moritz", region: "Engadina", lat: 46.4908, lng: 9.8355, themes: ["lakes", "mountains", "nature", "scenic_rail"], affordability: 3, transitFriendly: true, note: "Terminal oriental do Glacier Express e base para os lagos da Engadina." },

  // Itália
  { id: "it-florence", countryCode: "IT", city: "Florença", region: "Toscana", lat: 43.7696, lng: 11.2558, themes: ["cultural", "food", "city"], affordability: 2, transitFriendly: true },
  { id: "it-bolzano", countryCode: "IT", city: "Bolzano", region: "Dolomitas", lat: 46.4983, lng: 11.3548, themes: ["mountains", "nature"], affordability: 2, transitFriendly: true },
  { id: "it-como", countryCode: "IT", city: "Como", region: "Lago di Como", lat: 45.8081, lng: 9.0852, themes: ["lakes", "nature"], affordability: 2, transitFriendly: true },
  { id: "it-naples", countryCode: "IT", city: "Nápoles", region: "Campânia", lat: 40.8518, lng: 14.2681, themes: ["food", "cultural", "coast"], affordability: 1, transitFriendly: true },

  // Islândia
  { id: "is-reykjavik", countryCode: "IS", city: "Reiquiavique", region: "Sudoeste", lat: 64.1466, lng: -21.9426, themes: ["city", "nature"], affordability: 2, transitFriendly: true },
  { id: "is-vik", countryCode: "IS", city: "Vík", region: "Costa Sul", lat: 63.4187, lng: -19.0060, themes: ["nature", "coast"], affordability: 2, transitFriendly: false },
  { id: "is-akureyri", countryCode: "IS", city: "Akureyri", region: "Norte", lat: 65.6885, lng: -18.1262, themes: ["nature", "mountains"], affordability: 2, transitFriendly: false },

  // Noruega
  { id: "no-bergen", countryCode: "NO", city: "Bergen", region: "Fiordes do Oeste", lat: 60.3913, lng: 5.3221, themes: ["coast", "nature", "scenic_rail"], affordability: 2, transitFriendly: true },
  { id: "no-flam", countryCode: "NO", city: "Flåm", region: "Sognefjord", lat: 60.8630, lng: 7.1130, themes: ["nature", "scenic_rail"], affordability: 2, transitFriendly: true },
  { id: "no-oslo", countryCode: "NO", city: "Oslo", region: "Østlandet", lat: 59.9139, lng: 10.7522, themes: ["city", "cultural"], affordability: 2, transitFriendly: true },

  // Japão
  { id: "jp-tokyo", countryCode: "JP", city: "Tóquio", region: "Kantō", lat: 35.6762, lng: 139.6503, themes: ["city", "cultural", "food"], affordability: 2, transitFriendly: true },
  { id: "jp-kyoto", countryCode: "JP", city: "Quioto", region: "Kansai", lat: 35.0116, lng: 135.7681, themes: ["cultural", "nature"], affordability: 2, transitFriendly: true },
  { id: "jp-hakone", countryCode: "JP", city: "Hakone", region: "Fuji-Hakone", lat: 35.2324, lng: 139.1069, themes: ["nature", "mountains", "lakes"], affordability: 2, transitFriendly: true },
  { id: "jp-kanazawa", countryCode: "JP", city: "Kanazawa", region: "Hokuriku", lat: 36.5613, lng: 136.6562, themes: ["cultural", "food", "nature"], affordability: 1, transitFriendly: true },

  // Reino Unido — Escócia
  { id: "gb-glasgow", countryCode: "GB", city: "Glasgow", region: "Escócia Central", lat: 55.8642, lng: -4.2518, themes: ["city", "cultural", "scenic_rail"], affordability: 1, transitFriendly: true },
  { id: "gb-fortwilliam", countryCode: "GB", city: "Fort William", region: "Highlands", lat: 56.8198, lng: -5.1052, themes: ["mountains", "nature", "lakes", "scenic_rail"], affordability: 1, transitFriendly: true },
  { id: "gb-mallaig", countryCode: "GB", city: "Mallaig", region: "Costa Oeste", lat: 57.0064, lng: -5.8280, themes: ["coast", "nature", "scenic_rail"], affordability: 1, transitFriendly: true },
  { id: "gb-inverness", countryCode: "GB", city: "Inverness", region: "Highlands", lat: 57.4778, lng: -4.2247, themes: ["lakes", "nature", "city"], affordability: 1, transitFriendly: true },
  { id: "gb-edinburgh", countryCode: "GB", city: "Edimburgo", region: "Lothian", lat: 55.9533, lng: -3.1883, themes: ["city", "cultural", "food"], affordability: 2, transitFriendly: true },



  // Portugal
  { id: "pt-porto", countryCode: "PT", city: "Porto", region: "Norte", lat: 41.1579, lng: -8.6291, themes: ["city", "food", "cultural"], affordability: 1, transitFriendly: true },
  { id: "pt-lisboa", countryCode: "PT", city: "Lisboa", region: "Grande Lisboa", lat: 38.7223, lng: -9.1393, themes: ["city", "cultural", "food"], affordability: 1, transitFriendly: true },
  { id: "pt-regua", countryCode: "PT", city: "Peso da Régua", region: "Douro", lat: 41.1621, lng: -7.7889, themes: ["nature", "food", "scenic_rail"], affordability: 1, transitFriendly: true },
  { id: "pt-faro", countryCode: "PT", city: "Faro", region: "Algarve", lat: 37.0194, lng: -7.9304, themes: ["coast", "nature"], affordability: 1, transitFriendly: true },

  // Espanha
  { id: "es-granada", countryCode: "ES", city: "Granada", region: "Andaluzia", lat: 37.1773, lng: -3.5986, themes: ["cultural", "food"], affordability: 1, transitFriendly: true },
  { id: "es-sansebastian", countryCode: "ES", city: "San Sebastián", region: "País Basco", lat: 43.3183, lng: -1.9812, themes: ["food", "coast"], affordability: 2, transitFriendly: true },

  // Áustria
  { id: "at-salzburg", countryCode: "AT", city: "Salzburgo", region: "Salzkammergut", lat: 47.8095, lng: 13.0550, themes: ["lakes", "cultural", "mountains"], affordability: 2, transitFriendly: true },
  { id: "at-innsbruck", countryCode: "AT", city: "Innsbruck", region: "Tirol", lat: 47.2692, lng: 11.4041, themes: ["mountains", "nature"], affordability: 2, transitFriendly: true },
];

export function basesForCountry(countryCode?: string): RegionalBase[] {
  if (!countryCode) return [];
  return REGIONAL_BASES.filter((b) => b.countryCode === countryCode.toUpperCase());
}

/** Número realista de bases para o número de dias disponível. */
export function baseCountForDays(days: number): number {
  if (days <= 2) return 1;
  if (days <= 5) return 2;
  if (days <= 9) return 3;
  return 4;
}

/**
 * Escolhe bases a partir dos temas pedidos, privilegiando cobertura de temas,
 * acessibilidade em transportes (quando pedido) e preço.
 */
export function pickRegionalBases(input: {
  countryCode?: string;
  days: number;
  themes?: RegionalBase["themes"];
  preferTransit?: boolean;
  preferAffordable?: boolean;
}): RegionalBase[] {
  const pool = basesForCountry(input.countryCode);
  if (!pool.length) return [];
  const wanted = new Set(input.themes ?? []);
  const target = Math.min(baseCountForDays(input.days), pool.length);

  const score = (base: RegionalBase) => {
    let value = 0;
    for (const theme of base.themes) if (wanted.has(theme)) value += 3;
    if (input.preferTransit && base.transitFriendly) value += 2;
    if (input.preferAffordable) value += 3 - base.affordability;
    return value;
  };

  const chosen: RegionalBase[] = [];
  const covered = new Set<string>();
  const remaining = [...pool].sort((a, b) => score(b) - score(a));
  while (chosen.length < target && remaining.length) {
    // Prefere a base que acrescenta temas ainda não cobertos.
    remaining.sort((a, b) => {
      const novelty = (base: RegionalBase) => base.themes.filter((t) => wanted.has(t) && !covered.has(t)).length;
      return novelty(b) - novelty(a) || score(b) - score(a);
    });
    const next = remaining.shift()!;
    chosen.push(next);
    for (const theme of next.themes) covered.add(theme);
  }
  return chosen;
}
